import { Box, Container, Typography, Link, Divider, IconButton } from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

/* Subtle antique gold — warm, not bright amber */
const GOLD      = "#B09050";
const GOLD_GRAD = "linear-gradient(135deg,#9A7840,#C4A55A)";

/* Grey palette */
const BG        = "#1E2028";   /* dark grey, no blue tint */
const SURFACE   = "#252830";   /* slightly lighter */
const BORDER    = "#2E3240";
const TEXT      = "#9CA3AF";   /* grey-400 */
const TEXT_DIM  = "#6B7280";   /* grey-500 */
const TEXT_HEAD = "#D1D5DB";   /* grey-300 — column headings */

const COL = {
  Platform:  [
    { label: "KYC Management",  href: "/#solutions" },
    { label: "PEP Screening",   href: "/#solutions" },
    { label: "Risk Profiling",  href: "/#solutions" },
    { label: "Audit Trail",     href: "/#solutions" },
    { label: "FIU Reporting",   href: "/#solutions" },
  ],
  Company:   [
    { label: "About Us",        href: "/#about"           },
    { label: "Our Team",        href: "/#about"           },
    { label: "Careers",         href: null, modal: true },
    { label: "Privacy Policy",  href: "/privacy-policy"   },
    { label: "Terms of Service",href: "/terms-of-service" },
  ],
  Resources: [
    { label: "FATF Guidelines", href: "https://www.fatf-gafi.org/recommendations.html", external: true },
    { label: "UAE AML Law",     href: "https://u.ae/en/information-and-services/justice-safety-and-the-law/anti-money-laundering", external: true },
    { label: "CBUAE Circulars", href: "https://www.centralbank.ae/en/", external: true },
    { label: "FIU Portal",      href: "https://www.uaefiu.gov.ae/", external: true },
    { label: "Blog",            href: "/#blog" },
  ],
};

export default function Footer({ onRequestDemo }) {
  return (
    <Box sx={{ background: BG, borderTop: `1px solid ${BORDER}`, pt: { xs: 8, md: 10 }, pb: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "2fr 1fr 1fr 1fr 1.5fr" },
          gap: { xs: 6, md: 5 },
          mb: 7,
        }}>

          {/* Brand */}
          <Box>
            <Box sx={{ mb: 3 }}>
              <Box component="img" src="/logo.png" alt="AMLDiligence" sx={{ height: 38, display: "block", filter: "brightness(0) invert(1)" }} />
            </Box>

            <Typography sx={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.85, mb: 4, maxWidth: 280, fontFamily: "IBM Plex Sans, sans-serif" }}>
              Purpose-built AML compliance and monitoring software for Designated Non-Financial Businesses and Professions across the UAE and beyond.
            </Typography>

            <Box sx={{ display: "flex", gap: 1 }}>
              {[LinkedInIcon, TwitterIcon, FacebookIcon].map((Icon, i) => (
                <IconButton
                  key={i}
                  href="#"
                  aria-label={["LinkedIn","Twitter","Facebook"][i]}
                  sx={{
                    width: 34, height: 34, borderRadius: "8px",
                    border: `1px solid ${BORDER}`,
                    color: TEXT_DIM,
                    "&:hover": { color: GOLD, borderColor: `${GOLD}50`, background: "rgba(176,144,80,.06)" },
                    transition: "all .2s",
                  }}
                >
                  <Icon sx={{ fontSize: 16 }} />
                </IconButton>
              ))}
            </Box>
          </Box>

          {/* Link columns */}
          {Object.entries(COL).map(([heading, items]) => (
            <Box key={heading}>
              <Typography sx={{
                fontSize: 11, fontWeight: 600, color: GOLD,
                letterSpacing: "2px", textTransform: "uppercase", mb: 3,
                fontFamily: "IBM Plex Sans, sans-serif",
              }}>
                {heading}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                {items.map(({ label, href, external, modal }) => (
                  modal ? (
                    <Box
                      key={label}
                      component="button"
                      onClick={onRequestDemo}
                      sx={{
                        background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left",
                        fontSize: 13, color: TEXT_DIM,
                        fontFamily: "IBM Plex Sans, sans-serif",
                        "&:hover": { color: TEXT },
                        transition: "color .15s",
                      }}
                    >
                      {label}
                    </Box>
                  ) : (
                    <Link
                      key={label}
                      href={href}
                      underline="none"
                      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      sx={{
                        fontSize: 13, color: TEXT_DIM,
                        fontFamily: "IBM Plex Sans, sans-serif",
                        "&:hover": { color: TEXT },
                        transition: "color .15s",
                      }}
                    >
                      {label}
                    </Link>
                  )
                ))}
              </Box>
            </Box>
          ))}

          {/* Contact */}
          <Box>
            <Typography sx={{
              fontSize: 11, fontWeight: 600, color: GOLD,
              letterSpacing: "2px", textTransform: "uppercase", mb: 3,
              fontFamily: "IBM Plex Sans, sans-serif",
            }}>
              Contact
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 14, color: TEXT_DIM, mt: ".2em", flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.7, fontFamily: "IBM Plex Sans, sans-serif" }}>
                  Office 751, Tamani Art Offices,<br />Marasi Drive, Business Bay,<br />Dubai, UAE
                </Typography>
              </Box>
              {[
                { Icon: PhoneOutlinedIcon, href: "tel:+97144519930",  label: "+971 (4) 451 9930" },
                { Icon: PhoneOutlinedIcon, href: "tel:+971567517147", label: "+971 56 751 7147"   },
              ].map(({ Icon, href, label }) => (
                <Box key={label} sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  <Icon sx={{ fontSize: 13, color: TEXT_DIM, flexShrink: 0 }} />
                  <Link href={href} underline="none" sx={{ fontSize: 13, color: TEXT_DIM, fontFamily: "IBM Plex Sans, sans-serif", "&:hover": { color: GOLD }, transition: "color .15s" }}>
                    {label}
                  </Link>
                </Box>
              ))}
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <EmailOutlinedIcon sx={{ fontSize: 13, color: TEXT_DIM, flexShrink: 0 }} />
                <Box
                  component="button"
                  onClick={onRequestDemo}
                  sx={{
                    background: "none", border: "none", padding: 0, cursor: "pointer",
                    fontSize: 13, color: TEXT_DIM,
                    fontFamily: "IBM Plex Sans, sans-serif",
                    "&:hover": { color: GOLD },
                    transition: "color .15s",
                  }}
                >
                  info@amldiligence.com
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: BORDER, mb: 4 }} />

        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
          <Typography sx={{ fontSize: 12, color: TEXT_DIM, fontFamily: "IBM Plex Sans, sans-serif" }}>
            © {new Date().getFullYear()} AMLDiligence.com · ZAHADS Chartered Accountants. All rights reserved.
          </Typography>
          <Box sx={{ display: "flex", gap: 3 }}>
            {[["Privacy Policy","/privacy-policy"],["Terms","/terms-of-service"],["Cookie Policy","/cookie-policy"]].map(([t, href]) => (
              <Link key={t} href={href} underline="none" sx={{
                fontSize: 12, color: TEXT_DIM,
                fontFamily: "IBM Plex Sans, sans-serif",
                "&:hover": { color: TEXT },
                transition: "color .15s",
              }}>
                {t}
              </Link>
            ))}
          </Box>
        </Box>

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12, color: TEXT_DIM, fontFamily: "IBM Plex Sans, sans-serif" }}>
            Made with{" "}
            <Box component="span" sx={{ color: "#DC2626" }}>♥</Box>
            {" "}by our technology partner{" "}
            <Link href="https://hciexpert.com" target="_blank" rel="noopener noreferrer" underline="none" sx={{
              color: GOLD, fontWeight: 600,
              fontFamily: "IBM Plex Sans, sans-serif",
              "&:hover": { color: "#C4A55A" },
              transition: "color .15s",
            }}>
              HCI Expert
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
